import type { Placement } from '@popperjs/core';
import type { CSSProperties, ReactNode } from 'react';
import type { BoxProps } from '../Core';
import { Popover, usePopoverState } from '../Popover';
import type { PopoverStateProps } from '../Popover/types';
import { DropdownList } from './DropdownList';

export interface DropdownMenuProps extends Omit<PopoverStateProps, 'placement'> {
  /** The element that opens the dropdown when clicked (e.g. IconButton). */
  triggerElement: ReactNode;
  /** Menu content (DropdownItems, DropdownHeader, Divider). */
  children: ReactNode;
  /** Props passed to the inner Dropdown (e.g. minWidth, data-testid) */
  dropdownProps?: BoxProps & { 'data-testid'?: string };
  /** Target wrapper style (e.g. for alignment). Passed to Popover. */
  targetStyle?: CSSProperties;
  /** Portal target element when usePortal is true */
  portalElement?: HTMLElement;
  placement?: Placement;
}

const DEFAULT_PLACEMENT: Placement = 'bottom-start';

/**
 * A click-to-open dropdown menu built on {@link Popover} and {@link DropdownList}.
 *
 * Renders a trigger element alongside a popover-hosted {@link DropdownList} that
 * supports full keyboard navigation (ArrowUp/Down, Enter/Space) and nested
 * submenus when children include {@link DropdownItem}s with `submenuContent`.
 *
 * Accepts any mix of {@link DropdownItem}, {@link DropdownHeader}, and `Divider`
 * as children. Popover behavior (placement, portal, close-on-click-outside) is
 * configurable via props forwarded to {@link usePopoverState}.
 */
export function DropdownMenu({
  triggerElement,
  children,
  dropdownProps,
  targetStyle,
  portalElement,
  placement = DEFAULT_PLACEMENT,
  noPaddingAndBorder = true,
  closeOnClickOutside = true,
  trigger = 'click',
  ...popoverStateProps
}: DropdownMenuProps) {
  const popover = usePopoverState({
    ...popoverStateProps,
    placement,
    noPaddingAndBorder,
    closeOnClickOutside,
    trigger,
  });

  return (
    <Popover {...popover} targetStyle={targetStyle} portalElement={portalElement}>
      {triggerElement}
      <DropdownList {...dropdownProps}>{children}</DropdownList>
    </Popover>
  );
}
